/**
 * Currency utility functions for Yet2Book application
 * Handles currency conversion and formatting
 */

// Supported currencies
export const CURRENCIES = [
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' },
];

// Fixed exchange rates (relative to AED)
// In production, these should be fetched from a real-time API
export const EXCHANGE_RATES = {
  AED: 1,
  USD: 0.27,  // 1 AED = 0.27 USD
  EUR: 0.25,  // 1 AED = 0.25 EUR
  GBP: 0.21,  // 1 AED = 0.21 GBP
  INR: 22.76,  // 1 AED = 22.76 INR
  SAR: 1.02,  // 1 AED = 1.02 SAR
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {number} - Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) return amount;
  
  // Convert to AED first (base currency)
  const amountInAED = amount / EXCHANGE_RATES[fromCurrency];
  
  // Convert from AED to target currency
  const convertedAmount = amountInAED * EXCHANGE_RATES[toCurrency];
  
  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
};

/**
 * Format currency amount with symbol
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currencyCode) => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  if (!currency) return `${amount} ${currencyCode}`;
  
  // Format with thousand separators
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  
  return `${currency.symbol} ${formattedAmount}`;
};

/**
 * Get currency symbol by code
 * @param {string} currencyCode - Currency code
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  return currency ? currency.symbol : currencyCode;
};

/**
 * Get currency name by code
 * @param {string} currencyCode - Currency code
 * @returns {string} - Currency name
 */
export const getCurrencyName = (currencyCode) => {
  const currency = CURRENCIES.find(c => c.code === currencyCode);
  return currency ? currency.name : currencyCode;
};
